import React, { useState } from 'react';
import { tutorials } from '../data/supportData';

/**
 * Componente: TutorialGuide
 * Guia interativo passo a passo para aprender a usar a plataforma
 */
const TutorialGuide = () => {
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [activeTutorial, setActiveTutorial] = useState(null);
  const [currentStep, setCurrentStep] = useState(0);
  const [completedSteps, setCompletedSteps] = useState({});

  const categories = [
    { id: 'all', label: 'Todos', icon: '📚' },
    { id: 'owner', label: 'Barbeiro/Dono', icon: '💈' },
    { id: 'client', label: 'Cliente', icon: '👤' },
    { id: 'general', label: 'Geral', icon: '❓' },
  ];

  const filteredTutorials =
    selectedCategory === 'all'
      ? tutorials
      : tutorials.filter((t) => t.category === selectedCategory);

  const handleStartTutorial = (tutorial) => {
    setActiveTutorial(tutorial);
    setCurrentStep(0);
  };

  const handleNextStep = () => {
    if (activeTutorial && currentStep < activeTutorial.steps.length - 1) {
      // Marcar step atual como completo
      setCompletedSteps((prev) => ({
        ...prev,
        [`${activeTutorial.id}-${currentStep}`]: true,
      }));
      setCurrentStep((prev) => prev + 1);
    }
  };

  const handlePrevStep = () => {
    if (currentStep > 0) {
      setCurrentStep((prev) => prev - 1);
    }
  };

  const handleFinishTutorial = () => {
    // Marcar último step como completo
    if (activeTutorial) {
      setCompletedSteps((prev) => ({
        ...prev,
        [`${activeTutorial.id}-${currentStep}`]: true,
        [`${activeTutorial.id}-completed`]: true,
      }));
    }
    setActiveTutorial(null);
    setCurrentStep(0);
  };

  const isTutorialCompleted = (tutorialId) => {
    return completedSteps[`${tutorialId}-completed`] === true;
  };

  const getProgress = () => {
    if (!activeTutorial) return 0;
    return ((currentStep + 1) / activeTutorial.steps.length) * 100;
  };

  // ===== VISTA DO TUTORIAL ATIVO =====
  if (activeTutorial) {
    const step = activeTutorial.steps[currentStep];
    const isLastStep = currentStep === activeTutorial.steps.length - 1;

    return (
      <div className="max-w-3xl mx-auto">
        {/* Header do Tutorial */}
        <div className="bg-white rounded-xl sm:rounded-2xl shadow-sm border border-gray-200 overflow-hidden">
          <div className="bg-gradient-to-r from-indigo-600 to-indigo-700 p-4 sm:p-6 text-white">
            <div className="flex items-center justify-between mb-4">
              <button
                onClick={() => { setActiveTutorial(null); setCurrentStep(0); }}
                className="text-indigo-200 hover:text-white text-sm flex items-center gap-1 transition-colors"
              >
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="m15 18-6-6 6-6" />
                </svg>
                Voltar aos tutoriais
              </button>
              <span className="text-indigo-200 text-sm">
                {activeTutorial.estimatedTime}
              </span>
            </div>
            <h2 className="text-lg sm:text-2xl font-bold">
              {activeTutorial.icon} {activeTutorial.title}
            </h2>
            <p className="text-indigo-200 mt-1 text-sm sm:text-base">{activeTutorial.description}</p>

            {/* Barra de Progresso */}
            <div className="mt-4">
              <div className="flex items-center justify-between text-xs text-indigo-200 mb-1">
                <span>Progresso</span>
                <span>Passo {currentStep + 1} de {activeTutorial.steps.length}</span>
              </div>
              <div className="w-full bg-indigo-800/40 rounded-full h-2">
                <div
                  className="bg-white rounded-full h-2 transition-all duration-500"
                  style={{ width: `${getProgress()}%` }}
                />
              </div>
            </div>
          </div>

          {/* Steps Indicator */}
          <div className="px-3 sm:px-6 py-3 sm:py-4 border-b border-gray-100 bg-gray-50">
            <div className="flex items-center gap-1.5 sm:gap-1 overflow-x-auto pb-1 scrollbar-hide">
              {activeTutorial.steps.map((_, index) => (
                <button
                  key={index}
                  onClick={() => setCurrentStep(index)}
                  className={`flex-shrink-0 w-7 h-7 sm:w-8 sm:h-8 rounded-full flex items-center justify-center text-xs font-medium transition-all duration-200 ${
                    index === currentStep
                      ? 'bg-indigo-600 text-white shadow-md scale-110'
                      : completedSteps[`${activeTutorial.id}-${index}`]
                      ? 'bg-green-500 text-white'
                      : 'bg-gray-200 text-gray-500 hover:bg-gray-300'
                  }`}
                >
                  {completedSteps[`${activeTutorial.id}-${index}`] && index !== currentStep ? (
                    <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M20 6 9 17l-5-5" />
                    </svg>
                  ) : (
                    index + 1
                  )}
                </button>
              ))}
            </div>
          </div>

          {/* Conteúdo do Step */}
          <div className="p-4 sm:p-6">
            <div className="mb-4 sm:mb-6">
              <div className="flex items-start sm:items-center gap-3 mb-3">
                <div className="w-8 h-8 sm:w-10 sm:h-10 flex-shrink-0 bg-indigo-100 rounded-full flex items-center justify-center text-indigo-600 font-bold text-sm sm:text-lg">
                  {currentStep + 1}
                </div>
                <h3 className="text-base sm:text-xl font-semibold text-gray-900">
                  {step.title}
                </h3>
              </div>
              <p className="text-gray-600 leading-relaxed text-sm sm:text-base ml-0 sm:ml-[52px]">
                {step.description}
              </p>
            </div>

            {/* Dica (se houver) */}
            {step.tip && (
              <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 sm:p-4 ml-0 sm:ml-[52px] mb-4 sm:mb-6">
                <div className="flex items-start gap-2">
                  <span className="text-lg">💡</span>
                  <div>
                    <p className="text-sm font-medium text-amber-800">Dica</p>
                    <p className="text-sm text-amber-700 mt-0.5">{step.tip}</p>
                  </div>
                </div>
              </div>
            )}

            {/* Botões de Navegação */}
            <div className="flex items-center justify-between mt-6 sm:mt-8 pt-4 border-t border-gray-100">
              <button
                onClick={handlePrevStep}
                disabled={currentStep === 0}
                className="flex items-center gap-1 sm:gap-2 px-2 sm:px-4 py-2 text-xs sm:text-sm font-medium text-gray-600 hover:text-gray-800 disabled:text-gray-300 disabled:cursor-not-allowed transition-colors"
              >
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="m15 18-6-6 6-6" />
                </svg>
                Anterior
              </button>

              {isLastStep ? (
                <button
                  onClick={handleFinishTutorial}
                  className="flex items-center gap-1 sm:gap-2 px-4 sm:px-6 py-2 sm:py-2.5 bg-green-600 hover:bg-green-700 text-white text-xs sm:text-sm font-medium rounded-lg transition-colors shadow-sm"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M20 6 9 17l-5-5" />
                  </svg>
                  Concluir Tutorial
                </button>
              ) : (
                <button
                  onClick={handleNextStep}
                  className="flex items-center gap-1 sm:gap-2 px-4 sm:px-6 py-2 sm:py-2.5 bg-indigo-600 hover:bg-indigo-700 text-white text-xs sm:text-sm font-medium rounded-lg transition-colors shadow-sm"
                >
                  Próximo
                  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="m9 18 6-6-6-6" />
                  </svg>
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ===== LISTA DE TUTORIAIS =====
  return (
    <div>
      {/* Filtros de Categoria */}
      <div className="flex flex-wrap gap-1.5 sm:gap-2 mb-4 sm:mb-6">
        {categories.map((cat) => (
          <button
            key={cat.id}
            onClick={() => setSelectedCategory(cat.id)}
            className={`px-3 sm:px-4 py-1.5 sm:py-2 rounded-full text-xs sm:text-sm font-medium transition-all duration-200 ${
              selectedCategory === cat.id
                ? 'bg-indigo-600 text-white shadow-md'
                : 'bg-white text-gray-600 hover:bg-gray-100 border border-gray-200'
            }`}
          >
            {cat.icon} {cat.label}
          </button>
        ))}
      </div>

      {/* Grid de Tutoriais */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 sm:gap-4">
        {filteredTutorials.map((tutorial) => (
          <div
            key={tutorial.id}
            className={`bg-white rounded-xl border ${
              isTutorialCompleted(tutorial.id)
                ? 'border-green-200 bg-green-50/30'
                : 'border-gray-200'
            } p-4 sm:p-5 hover:shadow-md transition-all duration-200 cursor-pointer group`}
            onClick={() => handleStartTutorial(tutorial)}
          >
            <div className="flex items-start justify-between mb-2 sm:mb-3">
              <div className="flex items-center gap-2 sm:gap-3">
                <span className="text-2xl sm:text-3xl">{tutorial.icon}</span>
                <div>
                  <h3 className="font-semibold text-sm sm:text-base text-gray-900 group-hover:text-indigo-600 transition-colors">
                    {tutorial.title}
                  </h3>
                  <p className="text-xs sm:text-sm text-gray-500 mt-0.5 line-clamp-2">{tutorial.description}</p>
                </div>
              </div>
              {isTutorialCompleted(tutorial.id) && (
                <span className="flex-shrink-0 bg-green-100 text-green-700 text-xs font-medium px-2 py-1 rounded-full">
                  ✅ Concluído
                </span>
              )}
            </div>

            <div className="flex items-center justify-between mt-3 pt-3 border-t border-gray-100">
              <div className="flex items-center gap-3 text-xs text-gray-500">
                <span className="flex items-center gap-1">
                  <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <circle cx="12" cy="12" r="10" /><path d="M12 6v6l4 2" />
                  </svg>
                  {tutorial.estimatedTime}
                </span>
                <span className="flex items-center gap-1">
                  <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M8 2v4" /><path d="M16 2v4" /><rect width="18" height="18" x="3" y="4" rx="2" /><path d="M3 10h18" />
                  </svg>
                  {tutorial.steps.length} passos
                </span>
              </div>
              <span className="text-indigo-600 text-sm font-medium group-hover:translate-x-1 transition-transform inline-flex items-center gap-1">
                {isTutorialCompleted(tutorial.id) ? 'Rever' : 'Iniciar'}
                <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="m9 18 6-6-6-6" />
                </svg>
              </span>
            </div>
          </div>
        ))}
      </div>

      {filteredTutorials.length === 0 && (
        <div className="text-center py-12">
          <p className="text-gray-500">Nenhum tutorial encontrado nesta categoria.</p>
        </div>
      )}
    </div>
  );
};

export default TutorialGuide;
